import { Area, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { TrendingUp, TrendingDown, Minus, LineChart } from 'lucide-react';
import { formatMoney, cn } from '../../lib/utils';
import { Card, CardLink, EmptyState, ICON } from '../ui';
import { useGeld } from '../../lib/geld';
import type { DashboardTrendPoint } from '../../types';

/** Loon per periode (bruto of netto): wat de app berekent naast de loonstrook. */
export function TrendWidget({ trend: raw }: { trend: DashboardTrendPoint[] }) {
  const { modus: mode } = useGeld();
  const trend = raw.map((p) => ({
    ...p,
    app: mode === 'bruto' ? p.bruto : p.netto ?? 0,
    strook: mode === 'bruto' ? p.loonstrook_bruto : p.loonstrook_netto,
  }));
  const titel = mode === 'bruto' ? 'Bruto per periode' : 'Netto per periode';
  if (trend.length === 0) {
    return (
      <Card title="Loon per periode" icon={LineChart}>
        <EmptyState icon={LineChart} title="Nog geen afgeronde periodes" />
      </Card>
    );
  }

  const closed = trend.slice(0, -1);
  const avg = closed.length > 0 ? closed.reduce((acc, p) => acc + p.app, 0) / closed.length : trend[trend.length - 1].app;
  const first = closed[0]?.app ?? trend[0].app;
  const last = closed[closed.length - 1]?.app ?? trend[trend.length - 1].app;
  const trendPct = first > 0 ? ((last - first) / first) * 100 : 0;
  const TrendIcon = trendPct > 1 ? TrendingUp : trendPct < -1 ? TrendingDown : Minus;

  return (
    <Card
      title={titel}
      icon={LineChart}
      action={
        <div className="flex items-center gap-3">
          <span className="hidden sm:inline-flex">
            <CardLink to="/statistieken">Statistieken</CardLink>
          </span>
        </div>
      }
    >
      <div className="mb-4 grid grid-cols-3 gap-3">
        <div>
          <div className="t-label">Gemiddeld</div>
          <div className="t-num mt-1 text-base font-semibold text-slate-100">{formatMoney(avg)}</div>
        </div>
        <div>
          <div className="t-label">Laatst afgerond</div>
          <div className="t-num mt-1 text-base font-semibold text-slate-100">{formatMoney(last)}</div>
        </div>
        <div>
          <div className="t-label">Verloop</div>
          <div className={cn('t-num mt-1 inline-flex items-center gap-1 text-base font-semibold', trendPct > 1 ? 'text-money' : trendPct < -1 ? 'text-red-300' : 'text-slate-300')}>
            <TrendIcon className={ICON.sm} aria-hidden />
            {trendPct >= 0 ? '+' : ''}
            {trendPct.toFixed(1).replace('.', ',')}%
          </div>
        </div>
      </div>

      <div className="-mx-2 h-48">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={trend} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#2A2F37" vertical={false} />
            <XAxis dataKey="label" tick={{ fill: '#8A919C', fontSize: 12 }} tickLine={false} axisLine={false} />
            <YAxis
              tick={{ fill: '#8A919C', fontSize: 12 }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v: number) => `€${(v / 1000).toFixed(1).replace('.', ',')}k`}
              domain={[(min: number) => Math.floor((min - 200) / 500) * 500, (max: number) => Math.ceil((max + 200) / 500) * 500]}
              width={48}
            />
            <Tooltip
              cursor={{ stroke: '#454B54', strokeDasharray: '3 3' }}
              contentStyle={{ background: '#1A1F26', border: '1px solid #252B33', borderRadius: 8, fontSize: 13 }}
              labelStyle={{ color: '#E8EAED' }}
              formatter={(value: number, name: string) => [formatMoney(value), name === 'app' ? 'App' : 'Loonstrook']}
            />
            <Area type="monotone" dataKey="app" stroke="#CED2D8" strokeWidth={2} fill="#CED2D8" fillOpacity={0.06} isAnimationActive={false} />
            <Line type="monotone" dataKey="strook" stroke="#5CCB9A" strokeWidth={1.5} strokeDasharray="4 3" dot={false} connectNulls isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-3 flex items-center gap-4 text-xs text-slate-400">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-0.5 w-3 bg-slate-200" /> App
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-0.5 w-3 bg-money" /> Loonstrook
        </span>
      </div>
    </Card>
  );
}
